import { Link } from 'react-router';
import { formatEuro, type PublicEvent } from '@avh/shared';
import { Container } from '@/components/layout/PublicLayout';
import { ApiStateView } from '@/components/ui/ApiStateView';
import { buttonClasses } from '@/components/ui/buttonClasses';
import { useI18n } from '@/contexts/I18n';
import { dateParts, formatDateTime, localized } from '@/lib/format';
import { useApi } from '@/lib/useApi';
import { AvailabilityNote } from '@/pages/public/AvailabilityNote';

function EventCard({ event }: { event: PublicEvent }) {
  const { t, locale } = useI18n();
  const { day, month } = dateParts(event.startsAt, locale);
  const isFree = event.priceCents === 0;
  return (
    <article className="flex flex-col overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-black/5 sm:flex-row">
      <div className="flex shrink-0 items-center gap-3 bg-brand-900 px-6 py-4 text-white sm:w-32 sm:flex-col sm:justify-center sm:gap-0">
        <span className="text-4xl font-bold">{day}</span>
        <span className="text-sm font-semibold tracking-wide text-sun-300 uppercase">{month}</span>
      </div>
      <div className="flex flex-1 flex-col gap-3 p-5 sm:p-6">
        <div>
          <h3 className="text-xl font-bold text-brand-900">{localized(locale, event.title, event.titleEn)}</h3>
          {event.subtitle ? <p className="text-brand-700">{localized(locale, event.subtitle, event.subtitleEn)}</p> : null}
        </div>
        <p className="text-sm text-muted">
          {formatDateTime(event.startsAt, locale)} · {event.venueName}
        </p>
        <div className="mt-auto flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-col">
            <span className="font-semibold">
              {isFree ? t.event.free : `${formatEuro(event.priceCents, locale)} ${t.event.perTicket}`}
            </span>
            <AvailabilityNote event={event} />
          </div>
          <Link to={`/tickets/veranstaltung/${event.id}`} className={buttonClasses('primary')}>
            {isFree ? t.home.detailsFree : t.home.details}
          </Link>
        </div>
      </div>
    </article>
  );
}

export function HomePage() {
  const { t } = useI18n();
  const [state, reload] = useApi<PublicEvent[]>('/public/events');
  return (
    <>
      <section className="bg-brand-900 text-white">
        <div className="mx-auto max-w-6xl px-4 py-12 sm:py-16">
          <p className="text-sm font-semibold tracking-wide text-sun-300 uppercase">{t.home.eyebrow}</p>
          <h1 className="mt-2 text-3xl font-bold sm:text-5xl">{t.home.title}</h1>
          <p className="mt-4 max-w-2xl text-lg text-white/85">{t.home.intro}</p>
        </div>
      </section>
      <Container>
        <h2 className="mb-6 text-2xl font-bold text-brand-900">{t.home.upcoming}</h2>
        <ApiStateView state={state} onRetry={reload} loadingLabel={t.common.loading} retryLabel={t.common.retry}>
          {(events) =>
            events.length === 0 ? (
              <p className="text-muted">{t.home.none}</p>
            ) : (
              <div className="grid gap-5">
                {events.map((event) => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            )
          }
        </ApiStateView>
      </Container>
    </>
  );
}
